import { useEffect, useState } from 'react';
import { api } from '../../lib/auth';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Loader2, Play, FileWarning, CalendarX, AlertTriangle } from 'lucide-react';
import { ToggleRow, ChipInput } from './SettingsControls';

const WEEKDAYS = ['Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi', 'Pazar'];
const ADMIN_FLAGS = [
  { key: 'notify_firm_admins', label: 'Firma adminleri', desc: 'Sadece kendi firmasındaki çalışanlar' },
  { key: 'notify_full_admins', label: 'Full adminler', desc: 'Yetkili oldukları firmalar' },
  { key: 'notify_super_admins', label: 'Süper adminler', desc: 'Tüm çalışanlar' },
];
const fmt = (iso) => (iso ? new Date(iso).toLocaleString('tr-TR') : '—');
const hasAdmins = (c) => ADMIN_FLAGS.some((f) => c?.[f.key]) || (c?.extra_emails || []).length > 0;

const Recipients = ({ kind, c, set, expiryCfg }) => (
  <div className="space-y-2">
    <ToggleRow id={`${kind}-use-expiry`} label="Belge süresi uyarılarındaki adminleri kullan"
      desc="Yukarıdaki 'Kimlere gönderilsin?' seçimi" checked={c.use_expiry_recipients} onChange={(v) => set('use_expiry_recipients', v)} />
    {c.use_expiry_recipients ? (
      !hasAdmins(expiryCfg) && (
        <p className="flex items-center gap-1.5 text-xs text-amber-600" data-testid={`${kind}-no-recipient-warning`}>
          <AlertTriangle className="h-3.5 w-3.5" /> Yukarıda hiç admin veya ek e-posta seçili değil, bu özet kimseye gitmez.
        </p>
      )
    ) : (
      <>
        {!hasAdmins(c) && (
          <p className="flex items-center gap-1.5 text-xs text-amber-600" data-testid={`${kind}-own-no-recipient-warning`}>
            <AlertTriangle className="h-3.5 w-3.5" /> Hiç alıcı seçili değil, bu özet kimseye gitmez.
          </p>
        )}
        {ADMIN_FLAGS.map((f) => <ToggleRow key={f.key} id={`${kind}-${f.key}`} {...f} checked={c[f.key]} onChange={(v) => set(f.key, v)} />)}
        <Label className="pt-2 block">Ek e-posta adresleri</Label>
        <ChipInput testId={`${kind}-extra-email`} type="email" placeholder="ornek@firma.be" items={c.extra_emails}
          onAdd={(v) => set('extra_emails', [...new Set([...c.extra_emails, v.toLowerCase()])])}
          onRemove={(v) => set('extra_emails', c.extra_emails.filter((e) => e !== v))} />
      </>
    )}
  </div>
);

const DigestCard = ({ kind, icon: Icon, title, desc, c, meta, set, expiryCfg, onRun, running, children }) => (
  <Card data-testid={`digest-${kind}-card`}>
    <CardHeader className="pb-3">
      <div className="flex items-center justify-between">
        <div>
          <CardTitle className="text-base flex items-center gap-2"><Icon className="h-4 w-4" /> {title}</CardTitle>
          <CardDescription>{desc}</CardDescription>
        </div>
        <Switch data-testid={`digest-${kind}-enabled-switch`} checked={c.enabled} onCheckedChange={(v) => set('enabled', v)} />
      </div>
    </CardHeader>
    <CardContent className="grid md:grid-cols-2 gap-6">
      <div className="space-y-4">
        <div className="flex flex-wrap gap-3 items-end">
          {children}
          <div>
            <Label>Saat (UTC)</Label>
            <Input data-testid={`digest-${kind}-hour-input`} type="number" min={0} max={23} value={c.hour} className="w-24"
              onChange={(e) => set('hour', parseInt(e.target.value || '0', 10))} />
          </div>
        </div>
        <div className="text-xs text-slate-500 space-y-0.5">
          <p>Son gönderim (yerel saat): <span className="font-medium text-slate-700 dark:text-slate-300">{fmt(meta?.at)}</span>
            {meta?.at ? ` · ${meta.notifications} alıcı, ${meta.employees} çalışan` : ''}</p>
          <p>Sonraki (yerel saat): <span className="font-medium text-slate-700 dark:text-slate-300" data-testid={`digest-${kind}-next-run`}>{c.enabled ? fmt(meta?.next_run_at) : '—'}</span></p>
        </div>
        <Button data-testid={`digest-${kind}-run-now-btn`} variant="outline" size="sm" onClick={onRun} disabled={running}>
          {running ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Play className="mr-2 h-4 w-4" />} Şimdi Gönder
        </Button>
      </div>
      <Recipients kind={kind} c={c} set={set} expiryCfg={expiryCfg} />
    </CardContent>
  </Card>
);

export const DigestSettings = ({ expiryCfg }) => {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState('');

  const load = async () => setCfg((await api.get('/admin/admin-digests/config')).data.data);
  useEffect(() => { load().catch(() => toast.error('Özet ayarları yüklenemedi')); }, []);
  if (!cfg) return null;

  const setter = (kind) => (k, v) => setCfg((p) => ({ ...p, [kind]: { ...p[kind], [k]: v } }));
  const save = async () => {
    setSaving(true);
    try { await api.put('/admin/admin-digests/config', { missing: cfg.missing, expired: cfg.expired }); toast.success('Özet ayarları kaydedildi'); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Kaydedilemedi'); }
    finally { setSaving(false); }
  };
  const run = async (kind) => {
    setRunning(kind);
    try { const r = await api.post(`/admin/admin-digests/${kind}/run-now`); toast.success(r.data.message); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Gönderilemedi'); }
    finally { setRunning(''); }
  };

  return (
    <div className="space-y-6" data-testid="digest-settings">
      <DigestCard expiryCfg={expiryCfg} kind="missing" icon={FileWarning} title="Haftalık Eksik Belge Özeti"
        desc="Aktif çalışanların eksik belgeleri (ve isterseniz eksik profil bilgileri) adminlere tek listede gider"
        c={cfg.missing} meta={cfg.meta?.missing} set={setter('missing')} onRun={() => run('missing')} running={running === 'missing'}>
        <div>
          <Label>Gün</Label>
          <Select value={String(cfg.missing.weekday)} onValueChange={(v) => setter('missing')('weekday', parseInt(v, 10))}>
            <SelectTrigger className="w-36" data-testid="digest-missing-weekday-select"><SelectValue /></SelectTrigger>
            <SelectContent>{WEEKDAYS.map((d, i) => <SelectItem key={d} value={String(i)}>{d}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2 pb-2">
          <Switch data-testid="digest-missing-profile-fields-switch" checked={cfg.missing.include_profile_fields}
            onCheckedChange={(v) => setter('missing')('include_profile_fields', v)} />
          <span className="text-sm">Eksik profil bilgilerini de ekle</span>
        </div>
      </DigestCard>
      <DigestCard expiryCfg={expiryCfg} kind="expired" icon={CalendarX} title="Aylık Süresi Geçmiş Belgeler Özeti"
        desc="Süresi geçmiş ve hâlâ yenilenmemiş tüm belgeler, yenilenene kadar her ay listede kalır"
        c={cfg.expired} meta={cfg.meta?.expired} set={setter('expired')} onRun={() => run('expired')} running={running === 'expired'}>
        <div>
          <Label>Ayın günü</Label>
          <Input data-testid="digest-expired-day-input" type="number" min={1} max={28} value={cfg.expired.day_of_month} className="w-24"
            onChange={(e) => setter('expired')('day_of_month', parseInt(e.target.value || '1', 10))} />
        </div>
      </DigestCard>
      <div className="flex justify-end pr-16 pb-16">
        <Button data-testid="digest-save-btn" onClick={save} disabled={saving}>
          {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Özet Ayarlarını Kaydet
        </Button>
      </div>
    </div>
  );
};
